import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Icon, Timeline, nonHookMarkdown } from "@aidkitorg/component-library";
import { RichText } from "@aidkitorg/types/lib/survey";
import React, { useContext, useState } from "react";
import InterfaceContext from "../Context";
import { HistoryListWrapper } from "./historyUtils";
import { useLocalizedStrings } from "../Localization";
import { TimelineItem } from "@aidkitorg/aidkit/lib/application/activity_log";
import { FIELD_REF_REGEX, infoReplacer } from "@aidkitorg/types/lib/util";

export function ActivityLog(props: { uid: string, info?: Record<string, string | undefined>, relatedSupportCases?: { support_case: string; contact: string; channel: string }[] }) {
  const { uid, info, relatedSupportCases } = props;
  const getActivityLog = usePost("/applicant/get_activity_log");
  const [timeline, setTimeline] = useState<Record<string, TimelineItem[]>>({});
  const [timelineKeys, setTimelineKeys] = useState<string[]>([]);
  const [useUTC, setUseUTC] = useState(false);
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    const result = await getActivityLog({ applicant: uid, contacts: relatedSupportCases?.map((sc) => sc.contact) ?? [] });
    if (result?.timeline) {
      setTimeline(result.timeline);
      setTimelineKeys(Object.keys(result.timeline).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()));
    }
  }, [uid, relatedSupportCases]);

  if (timelineKeys.length === 0) {
    return <div className="p-4 text-gray-500">{L.applicant.activity_log.no_activity_to_display}</div>;
  }

  const fieldUpdatedTemplate = L.applicant.activity_log.field_updated_to;
  const [beforeField, afterField] = fieldUpdatedTemplate.split("$field");
  const [beforeValue, afterValue] = afterField.split("$value");

  const userUpdatedTemplate = L.applicant.activity_log.user_updated;
  const [assigneeBeforeField, assigneeAfterField] = userUpdatedTemplate.split("$field");
  const [assigneeBeforeValue, assigneeAfterValue] = assigneeAfterField.split("$user");

  const surveyResendTemplate = L.applicant.activity_log.survey_resend;
  const [surveyResendBeforeName, surveyResendAfterName] = surveyResendTemplate.split("$name");
  const [surveyResendBeforeSurvey, surveyResendAfterSurvey] = surveyResendAfterName.split("$survey");

  return (
    <HistoryListWrapper useUTC={useUTC} onCheckedChange={setUseUTC}>
      <div className="pt-4">
        <Timeline>
          {timelineKeys.flatMap((key, index) => {
            const items = timeline[key];
            const fieldChanges = items.filter((item): item is Extract<TimelineItem, { kind: 'field_change' }> => item.kind === 'field_change');
            const noteItems = items.filter((item): item is Extract<TimelineItem, { kind: 'note' | 'support_case_note' }> => item.kind === 'note' || item.kind === 'support_case_note');
            const livenessItems = items.filter((item): item is Extract<TimelineItem, { kind: 'liveness_session' }> => item.kind === 'liveness_session');

            const nodes: React.ReactNode[] = [];
            if (fieldChanges.length > 0) {
              nodes.push(
                <Timeline.Item key={`${key}-${index}`} time={key}>
                  <ul className={`${fieldChanges.length > 1 ? 'list-disc pl-4 marker:text-gray-400' : 'pl-2'} space-y-1`}>
                    {fieldChanges.map((item, itemIndex) => (
                      <li key={`${item.key}-${item.value}-${itemIndex}`}>
                        {item.message ? (
                          <div className="font-bold">{nonHookMarkdown((item.message[context.lang] || item.message['en'])?.replace(FIELD_REF_REGEX, (match) => infoReplacer(match, { info: { ...info, [item.key]: item.value }, uid })))}</div>
                        ) : item.key.startsWith('resend_survey_') ? (
                          <div>
                            {surveyResendBeforeName}
                            <span className="font-bold">{item.userLookup}</span> 
                            {surveyResendBeforeSurvey}
                            <span className="font-bold">{item.key.split('resend_survey_')[1]}</span> 
                            {surveyResendAfterSurvey}
                          </div>
                        )
                          : item.userLookup ? (
                            <div>
                              {assigneeBeforeField}
                              <span className="font-bold">{item.key}</span>
                              {assigneeBeforeValue}
                              <span className="font-bold">{item.userLookup}</span>
                              {assigneeAfterValue}
                            </div>
                          )
                            : (
                              <div>
                                {beforeField}
                                <span className="font-bold">{item.key}</span>
                                {beforeValue}
                                <span className="font-bold">{item.value}</span>
                                {afterValue}
                              </div>
                            )}
                      </li>
                    ))}
                  </ul>
                </Timeline.Item>
              );
            }
            noteItems.forEach((item, itemIndex) => {
              nodes.push(
                <Timeline.Item key={`${key}-note-${itemIndex}`} time={item.date}>
                  <div className="pl-2">
                    <div className="flex items-center">
                      <span className="font-bold">{L.applicant.activity_log.note_added}</span>
                      {item.kind === 'support_case_note' ? <Badge variant="neutral" className="ml-2 text-xs">{L.applicant.activity_log.support_case}</Badge> : null}
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mt-1.5 text-sm">
                      {item.note}
                    </div>
                    <div className="text-gray-500 text-xs mt-1.5">
                      {item.author}
                    </div>
                  </div>
                </Timeline.Item>
              );
            });
            livenessItems.forEach((item, itemIndex) => {
              const stageVariant = item.stage === 'done' ? 'success' : 'warning';
              nodes.push(
                <Timeline.Item key={`${key}-liveness-${itemIndex}`} time={item.date}>
                  <div className="pl-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{L.applicant.activity_log.liveness}</span>
                      <Badge variant={stageVariant} className="text-xs">{item.stage === 'done' ? 'Done' : 'Incomplete'}</Badge>
                    </div>
                    <Accordion type="single" className="mt-1 w-[200px]">
                      <AccordionItem value="captures" className="border-b-0">
                        <AccordionTrigger className="py-1.5 px-0 text-sm hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          {L.applicant.activity_log.liveness_details}
                        </AccordionTrigger>
                        <AccordionContent className="px-0 pb-2 pt-0">
                          <div className="flex flex-col gap-y-0.5 text-sm text-gray-600">
                            <span className="inline-flex items-center gap-1">{L.applicant.activity_log.liveness_front} {item.frontExists 
                              ? <Icon name="CheckIcon" type="solid" className="h-5 w-5 text-green-500" aria-hidden /> 
                              : <Icon name="XMarkIcon" type="solid" className="h-5 w-5 text-red-500" aria-hidden />}</span>
                            <span className="inline-flex items-center gap-1">{L.applicant.activity_log.liveness_back} {item.backExists 
                              ? <Icon name="CheckIcon" type="solid" className="h-5 w-5 text-green-500" aria-hidden /> 
                              : <Icon name="XMarkIcon" type="solid" className="h-5 w-5 text-red-500" aria-hidden />}</span>
                            <span className="inline-flex items-center gap-1">{L.applicant.activity_log.liveness_selfie} {item.selfieExists 
                              ? <Icon name="CheckIcon" type="solid" className="h-5 w-5 text-green-500" aria-hidden /> 
                              : <Icon name="XMarkIcon" type="solid" className="h-5 w-5 text-red-500" aria-hidden />}</span>
                            <span className="inline-flex items-center gap-1">{L.applicant.activity_log.liveness_stage}: {item.stage}</span>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </Timeline.Item>
              );
            });
            return nodes;
          })}
        </Timeline>
      </div>
    </HistoryListWrapper>
  );
}
